// src/components/skills/SkillNewVersionModal.tsx
//
// Modal "Nova versão da skill" (Reforma UX §6.7, SKL-04, wireframe dlgNewSkillVer).
// POST /v1/skills/:slug/versions — backend aceita apenas { version? } (regex \d+.\d+,
// auto-incrementa minor quando omitido). A nova versão CLONA a SKILL.md atual e
// nasce status='draft' (imutável por versão).
//
// DIVERGÊNCIA HTML×backend (reportada): o wireframe tem campos "Base" e
// "O que muda" (Mario geraria a nova SKILL.md a partir da mudança) — o endpoint
// real não recebe nada disso. Mantemos só o que o backend honra.

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { GitBranch, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';

export interface SkillNewVersionModalProps {
  open: boolean;
  onClose: () => void;
  skill: { slug: string; version: string };
  onCreated: (msg: string) => void;
}

export function SkillNewVersionModal({ open, onClose, skill, onCreated }: SkillNewVersionModalProps) {
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    const trimmed = version.trim();
    if (trimmed && !/^\d+\.\d+$/.test(trimmed)) {
      setError('Versão deve ser MAJOR.MINOR (ex: 1.5). Deixe vazio pra auto-incrementar o minor.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await api.createSkillVersion(skill.slug, trimmed ? { version: trimmed } : undefined);
      onCreated(
        `Versão ${result.version} criada (a partir de ${result.from_version}, status=${result.status}).`,
      );
      setVersion('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-raios-accent-text" aria-hidden />
            Nova versão da skill
          </DialogTitle>
          <DialogDescription>
            Skill <span className="font-mono">{skill.slug}</span> · versão atual{' '}
            <span className="font-mono text-raios-accent-text">v{skill.version}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="skill-new-version-input">
              Nova versão (opcional)
            </label>
            <Input
              id="skill-new-version-input"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="ex: 1.5 — vazio auto-incrementa o minor"
            />
          </div>
          <p className="text-xs text-raios-muted">
            Cada versão é <strong>imutável</strong>: a nova clona a SKILL.md atual no storage e
            nasce com status <span className="font-mono">draft</span>.
          </p>
          {error && (
            <div className="rounded border border-red-300 bg-red-50 dark:bg-red-950/40 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" aria-hidden />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="brand" onClick={handleCreate} loading={loading}>
            Criar versão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
